import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const AttendanceFeedHealthReport = withListOverflow(FeedHealthListOwner);
// ATTENDANCE-FEED-HEALTH-REPORT
//
// Admin-only, read-only Envoy attendance completeness worklist.
// Display-only: no fetching, routing, or mutation. Aggregate office data only.

import React from "react";
import { T } from "../../app/tokens.js";

const NA = "Not available";
const UNK = "Unknown";

const STATUS_STYLE = {
  Reporting: { bg: T.ragGreenBg, fg: T.ragGreenText, dot: T.greenDeep, desc: "full coverage" },
  Partial: { bg: T.ragAmberBg, fg: T.ragAmberText, dot: T.ragAmber, desc: "some days missing" },
  "Feed dark": { bg: T.ragRedBg, fg: T.ragRed, dot: T.ragRed, desc: "stopped reporting" },
  Unmapped: { bg: T.blueSoft, fg: T.blue, dot: T.blue, desc: "no source connected" },
  "Not expected": { bg: T.lightGray, fg: T.mid, dot: T.mid, desc: "outside attendance scope" },
  "Should exclude": { bg: T.lightGray, fg: T.mid, dot: T.light, desc: "closed, still counted" },
  Unknown: { bg: T.lightGray, fg: T.mid, dot: T.light, desc: "state not available" },
};
const LEGEND = ["Reporting", "Partial", "Feed dark", "Unmapped", "Not expected", "Should exclude", "Unknown"];
const ATTENTION = new Set(["Partial", "Feed dark", "Unmapped", "Should exclude"]);

const DAY_STYLE = {
  reported: { bg: T.greenDeep, label: "reported" },
  missing: { bg: T.ragRed, label: "missing" },
  non_working: { bg: T.utilizationTrack, label: "non-working" },
  unknown: { bg: T.white, label: "unknown", border: `1px dashed ${T.borderDark}` },
};

const CAUSE = {
  "Feed dark": {
    tag: "Feed outage", owner: "IT",
    text: "Mapped office whose feed stopped reporting. Check the Envoy device or connection.",
  },
  Partial: {
    tag: "Intermittent", owner: "IT",
    text: "Reporting, but with gaps through the period. Verify the reader is syncing reliably.",
  },
  Unmapped: {
    tag: "No source mapping", owner: "IT",
    text: "Office is not connected to an Envoy source. Add the location mapping.",
  },
  "Should exclude": {
    tag: "Closed office, still counted", owner: "Workplace",
    text: "Lifecycle data says this office is closed, yet it remains in the eligible population and lowers coverage.",
  },
  Unknown: {
    tag: "State not available", owner: null,
    text: "The report could not classify this office. Review its source data.",
  },
};

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const fmtN = (v) => (num(v) == null ? NA : v.toLocaleString());
const pct = (v) => (num(v) == null ? NA : `${(v * 100).toFixed(1)}%`);
const txt = (v, f = UNK) => (v == null || String(v).trim() === "" ? f : String(v));
const pair = (o, e) => (num(o) == null || num(e) == null ? NA : `${o} / ${e}`);

function parseDate(v) {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  const d = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
function fmtDate(v, withYear) {
  const d = parseDate(v);
  if (!d) return NA;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: withYear ? "numeric" : undefined, timeZone: "UTC" });
}
function fmtMonth(v) {
  if (!v) return NA;
  const d = parseDate(/^\d{4}-\d{2}$/.test(String(v)) ? `${v}-01` : v);
  return d ? d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" }) : String(v);
}
function ago(v) {
  const d = parseDate(v);
  if (!d || !String(v).includes("T")) return d ? fmtDate(v, true) : NA;
  const mins = Math.max(1, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}
function exactTimestamp(v) {
  const d = parseDate(v);
  return d && String(v).includes("T")
    ? d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : NA;
}

const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, boxShadow: T.cardShadow };
const eyebrow = { color: T.mid, fontSize: T.fsXxxs5, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase" };
const th = { padding: "9px 10px", borderBottom: `1px solid ${T.border}`, background: T.tableHeadBg, ...eyebrow, textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: "9px 10px", borderTop: `1px solid ${T.border}`, color: T.dark, fontSize: T.fsSm5, verticalAlign: "top" };
const mono = { fontVariantNumeric: "tabular-nums" };

function Pill({ status, id }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.Unknown;
  return (
    <span data-testid={`feed-status-${id}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 999, padding: "3px 8px", background: s.bg, color: s.fg, fontSize: T.fsXxxs5, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot }} />
      {STATUS_STYLE[status] ? status : UNK}
    </span>
  );
}

function SectionHead({ title, count, note, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "22px 0 8px" }}>
      <h2 style={{ ...eyebrow, margin: 0, fontSize: T.fsXs }}>{title}</h2>
      <span style={{ color: T.light, fontSize: T.fsXs }}>· {count}</span>
      {note && <span style={{ color: T.light, fontSize: T.fsXs }}>{note}</span>}
    </div>
  );
}

function DayGrid({ days, id }) {
  const list = Array.isArray(days) ? days : [];
  if (!list.length) {
    return <div data-testid={`feed-grid-${id}`} style={{ marginTop: 12, color: T.light, fontSize: T.fsXs }}>Day detail {NA.toLowerCase()}.</div>;
  }
  return (
    <div style={{ marginTop: 12 }}>
      <div data-testid={`feed-grid-${id}`} role="list" style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {list.map((d, i) => {
          const st = DAY_STYLE[d?.state] ?? DAY_STYLE.unknown;
          const dt = parseDate(d?.date);
          const label = `${fmtDate(d?.date)}: ${st.label}`;
          return (
            <div key={`${d?.date}-${i}`} role="listitem" title={label} aria-label={label} data-testid={`feed-day-${id}-${d?.date ?? i}`}
              style={{ width: 24, textAlign: "center" }}>
              <div style={{ height: 16, borderRadius: 3, background: st.bg, border: st.border }} />
              <div style={{ marginTop: 2, color: T.light, fontSize: T.fsMin, ...mono }}>{dt ? fmtDate(d.date) .replace(/^\w+ /, "") : "?"}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 6, color: T.light, fontSize: T.fsXs }}>
        Each square is a day, labelled by day of month: green reported, red missing, gray non-working, dashed unknown.
      </div>
    </div>
  );
}

function AttentionCard({ o }) {
  const id = o.officeId ?? o.name;
  const c = CAUSE[o.status] ?? CAUSE.Unknown;
  const s = STATUS_STYLE[o.status] ?? STATUS_STYLE.Unknown;
  const lc = o.lifecycle ?? {};
  const mapping = o.mappingState === "mapped" ? "mapped to Envoy" : o.mappingState === "unmapped" ? "unmapped" : `mapping ${UNK.toLowerCase()}`;
  return (
    <article data-testid={`feed-attention-${id}`} style={{ ...card, padding: "14px 16px", marginBottom: 10, borderLeft: `3px solid ${s.dot}` }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 22px", alignItems: "flex-start" }}>
        <div style={{ minWidth: 200 }}>
          <strong style={{ color: T.dark, fontSize: T.fsMd5 }}>{txt(o.name, NA)}</strong>
          <div style={{ color: T.mid, fontSize: T.fsSm, marginTop: 2 }}>{[o.region, o.market].filter(Boolean).join(" · ") || UNK}</div>
          <div style={{ color: T.light, fontSize: T.fsXs, marginTop: 6 }}>
            Last reported: {o.lastReportedDate
              ? fmtDate(o.lastReportedDate)
              : o.lastReportedState === "no_reports"
                ? "never in period"
                : UNK.toLowerCase()} · {mapping}
          </div>
        </div>
        <div style={{ minWidth: 110 }}>
          <div style={{ color: T.dark, fontSize: T.fsLg6, fontWeight: T.fwDisplay, ...mono }}>{pair(o.observedOfficeDays, o.eligibleOfficeDays)}</div>
          <div style={{ ...eyebrow, fontSize: T.fsXxs }}>days this month</div>
          <div style={{ marginTop: 6 }}><Pill status={o.status} id={id} /></div>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ color: s.fg, fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 3 }}>{c.tag}</div>
          <div style={{ color: T.dark, fontSize: T.fsBase, lineHeight: 1.5 }}>{c.text}</div>
          {o.lifecycleReason && <div data-testid={`feed-reason-${id}`} style={{ color: T.mid, fontSize: T.fsSm, marginTop: 4 }}>{o.lifecycleReason}</div>}
          <div data-testid={`feed-owner-${id}`} style={{ marginTop: 6, color: T.light, fontSize: T.fsXs }}>
            Owner: <strong style={{ color: T.mid }}>{txt(o.owner || c.owner)}</strong>
          </div>
          {(lc.stage || lc.exitDate || lc.reopenDate || lc.leaseStatus) && (
            <div style={{ marginTop: 4, color: T.light, fontSize: T.fsXs }}>
              Lifecycle: {txt(lc.stage)} · exit {lc.exitDate ? fmtDate(lc.exitDate, true) : NA} · lease {txt(lc.leaseStatus)}
            </div>
          )}
        </div>
      </div>
      <DayGrid days={o.days} id={id} />
    </article>
  );
}

function Table({ testid, heads, rows, empty }) {
  return (
    <div style={{ ...card, overflowX: "auto" }}>
      <table data-testid={testid} style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
        <thead><tr>{heads.map(([h, right]) => <th key={h} style={{ ...th, textAlign: right ? "right" : "left" }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows : (
            <tr><td colSpan={heads.length} style={{ ...td, textAlign: "center", color: T.mid }}>{empty}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ k, v, sub, warn, id }) {
  return (
    <div data-testid={`feed-kpi-${id}`} style={{ ...card, padding: "11px 13px", transition: "box-shadow .15s", minWidth: 0 }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = T.shadowHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.cardShadow; }}>
      <div style={eyebrow}>{k}</div>
      <div style={{ color: warn ? T.ragRed : T.dark, fontSize: T.fsXl, fontWeight: T.fwDisplay, marginTop: 3, ...mono }}>{v}</div>
      <div style={{ color: T.light, fontSize: T.fsXs, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function Notice({ testid, children, tone }) {
  const bad = tone === "error";
  return (
    <div data-testid={testid} role={bad ? "alert" : "status"} style={{ ...card, padding: "30px 16px", textAlign: "center", color: bad ? T.ragRed : T.mid, background: bad ? T.ragRedBg : T.card, borderColor: bad ? T.ragRedBorder : T.border, fontSize: T.fsBase }}>
      {children}
    </div>
  );
}

function Skeleton() {
  const bar = (w, h) => <div style={{ width: w, height: h, borderRadius: 4, background: T.lightGray }} />;
  return (
    <div data-testid="feed-health-loading" aria-busy="true">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, margin: "14px 0" }}>
        {[0, 1, 2, 3, 4].map((i) => <div key={i} style={{ ...card, padding: 14, display: "grid", gap: 8 }}>{bar(80, 9)}{bar(60, 22)}{bar(100, 9)}</div>)}
      </div>
      <div style={{ ...card, padding: 14, display: "grid", gap: 8 }}>{bar("100%", 12)}{bar("80%", 12)}{bar("90%", 12)}</div>
    </div>
  );
}

function FeedHealthListOwner({ report, loading, error, onBack } = {}) {
  const period = report?.reportingPeriod ?? {};
  const sync = report?.sync ?? {};
  const sum = report?.summary ?? {};
  const offices = Array.isArray(report?.offices) ? report.offices.filter(Boolean) : [];
  const monthLabel = fmtMonth(period.month);
  const short = period.month ? monthLabel.split(" ")[0].slice(0, 3) : "Period";

  const attention = offices.filter((o) => ATTENTION.has(o.status));
  const unclassified = offices.filter((o) => o.status === "Unknown" || !STATUS_STYLE[o.status]);
  const reporting = offices.filter((o) => o.status === "Reporting");
  const excluded = offices.filter((o) => o.status === "Not expected");
  const attentionByOwner = new Map();
  for (const office of attention) {
    const owner = txt(office.owner || CAUSE[office.status]?.owner, "Owner not available");
    if (!attentionByOwner.has(owner)) attentionByOwner.set(owner, []);
    attentionByOwner.get(owner).push(office);
  }
  const causes = ["Feed dark", "Partial", "Unmapped", "Should exclude"]
    .map((k) => [k, attention.filter((o) => o.status === k).length])
    .filter(([, n]) => n > 0);
  const missingDays = num(sum.missingOfficeDays);
  const needs = num(sum.needsAttention) ?? (report ? attention.length : null);
  const officesWithMissingDays = num(sum.officesWithMissingDays)
    ?? offices.filter((o) => num(o.eligibleOfficeDays) > num(o.observedOfficeDays)).length;

  const hasData = !!report && offices.length > 0;

  return (
    <div data-testid="attendance-feed-health-report" style={{ color: T.dark }}>
      {onBack && (
        <button type="button" data-testid="feed-health-back-btn" onClick={onBack}
          style={{ border: "none", background: "transparent", color: T.blue, fontFamily: "inherit", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer", padding: "0 0 8px" }}>
          ← Back to integrations
        </button>
      )}
      <h1 style={{ margin: "0 0 3px", fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark }}>Attendance Feed Health</h1>
      <p data-testid="feed-health-subtitle" style={{ margin: "0 0 14px", color: T.mid, fontSize: T.fsSm }}>
        Admin · read-only · Which offices are reporting Envoy attendance, and which need IT or Workplace attention · Reporting period {monthLabel}
      </p>

      <section data-testid="feed-health-explainer" style={{ background: T.adminExplainerBg, border: `1px solid ${T.adminExplainerBorder}`, borderRadius: 7, padding: "12px 14px", marginBottom: 12 }}>
        <div style={{ ...eyebrow, color: T.adminExplainerLabel }}>What this report does</div>
        <p style={{ margin: "6px 0 0", color: T.adminExplainerFg, fontSize: T.fsBase, lineHeight: 1.5, maxWidth: 860 }}>
          Shows which offices sent Envoy attendance during {monthLabel}, which need IT or Workplace follow-up, and which are correctly outside the eligible population. Feed completeness uses the same eligible and observed office-day definitions as the Offices coverage metric. This report is read-only and changes no source data.
        </p>
        <div style={{ marginTop: 9, paddingTop: 9, borderTop: `1px solid ${T.adminExplainerBorder}`, color: T.adminExplainerLabel, fontSize: T.fsXs }}>
          Matches <strong style={{ color: T.adminExplainerFg, background: T.blueSoft, padding: "1px 6px", borderRadius: 4 }}>the {monthLabel} attendance coverage number</strong>
          {num(period.coverageRatio) != null && <> (currently {pct(period.coverageRatio)})</>} and the "still finalizing" notices across the app.
        </div>
      </section>

      <div data-testid="feed-health-legend" aria-label="Status legend" style={{ ...card, display: "flex", flexWrap: "wrap", gap: "6px 16px", padding: "8px 10px", marginBottom: 12, color: T.mid, fontSize: T.fsXs }}>
        <strong style={{ color: T.dark }}>Status:</strong>
        {LEGEND.map((k) => (
          <span key={k} data-testid={`feed-legend-${k.toLowerCase().replace(/\s+/g, "-")}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: STATUS_STYLE[k].dot }} />
            <strong style={{ color: STATUS_STYLE[k].fg }}>{k}</strong> {STATUS_STYLE[k].desc}
          </span>
        ))}
      </div>
      <p style={{ color: T.light, fontSize: T.fsXs, margin: "-5px 1px 11px" }}>
        Reporting is at least 95% coverage. Feed dark means no report this period or at least three trailing eligible days without a report; unknown eligible days are not counted as missing.
      </p>

      {loading ? <Skeleton /> : error ? (
        <Notice testid="feed-health-error" tone="error">
          Unable to load the attendance feed health report.
          {typeof error?.message === "string" && error.message ? <div style={{ marginTop: 4, fontSize: T.fsSm }}>{error.message}</div> : null}
        </Notice>
      ) : !hasData ? (
        <Notice testid="feed-health-empty">No office attendance data is available for this reporting period.</Notice>
      ) : (
        <>
          <div data-testid="feed-health-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 12 }}>
            <Kpi id="offices-reporting" k="Offices reporting" v={num(sum.officesReporting) == null ? NA : <>{sum.officesReporting}<span style={{ fontSize: T.fsLg, color: T.light }}> / {fmtN(sum.eligibleOffices)}</span></>} sub={`sending ${short} data`} />
            <Kpi id="needs-attention" k="Need attention" v={needs == null ? NA : needs} sub="on IT / Workplace" warn={needs > 0} />
            <Kpi id="coverage" k={`${short} coverage`} v={pct(period.coverageRatio)} sub={`${pair(period.observedOfficeDays, period.eligibleOfficeDays)} office-days`} />
            <Kpi id="missing-days" k="Office-days missing" v={fmtN(missingDays)} sub={`across ${fmtN(officesWithMissingDays)} offices`} />
            <Kpi id="last-sync" k="Last Envoy sync" v={<span style={{ fontSize: T.fsLg }} title={sync.lastSuccessAt ? exactTimestamp(sync.lastSuccessAt) : undefined}>{sync.lastSuccessAt ? ago(sync.lastSuccessAt) : NA}</span>} sub={sync.lastSuccessAt ? exactTimestamp(sync.lastSuccessAt) : "no success recorded"} />
          </div>

          <div data-testid="feed-health-lede" style={{ ...card, padding: "11px 14px", fontSize: T.fsBase }}>
            <strong>{attention.length} {attention.length === 1 ? "office needs" : "offices need"} IT or Workplace follow-up for {monthLabel}.</strong>{" "}
            <span data-testid="feed-health-causes" style={{ color: T.mid }}>
              {causes.length ? causes.map(([k, n]) => `${n} ${k.toLowerCase()}`).join(", ") : "No causes to report."}
              {unclassified.length > 0 && ` · ${unclassified.length} unknown state${unclassified.length === 1 ? "" : "s"} shown separately`}
            </span>
          </div>

          <SectionHead testid="feed-section-attention" title="Needs attention" count={attention.length} />
          {attention.length ? [...attentionByOwner.entries()].map(([owner, ownerOffices]) => (
            <section key={owner} data-testid={`feed-owner-group-${owner.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{ marginTop: 12 }}>
              <h3 style={{ ...eyebrow, margin: "0 0 7px", fontSize: T.fsXs }}>
                Owner: {owner} <span style={{ color: T.light, fontWeight: T.fwRegular }}>· {ownerOffices.length}</span>
              </h3>
              {ownerOffices.map((o, i) => <AttentionCard key={o.officeId ?? i} o={o} />)}
            </section>
          )) : (
            <Notice testid="feed-attention-empty">No offices need attention.</Notice>
          )}

          {unclassified.length > 0 && (
            <>
              <SectionHead testid="feed-section-unclassified" title="Unknown state" count={unclassified.length} note="- source evidence is incomplete" />
              <p style={{ color: T.mid, fontSize: T.fsSm, margin: "0 0 8px" }}>
                These offices are not assigned to an owner or counted as a feed outage because mapping or eligible-day evidence is unknown.
              </p>
              {unclassified.map((o, i) => <AttentionCard key={o.officeId ?? i} o={o} />)}
            </>
          )}

          <SectionHead testid="feed-section-reporting" title="Reporting normally" count={reporting.length} />
          <Table testid="feed-reporting-table" empty="No offices are reporting normally."
            heads={[["Office"], ["Region"], ["Status"], [`${short} coverage`, true], ["Last reported", true], ["Source"]]}
            rows={reporting.map((o, i) => {
              const id = o.officeId ?? i;
              return (
                <tr key={id} data-testid={`feed-reporting-row-${id}`} onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }} onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}>
                  <td style={{ ...td, fontWeight: T.fwSemibold }}>{txt(o.name, NA)}</td>
                  <td style={td}>{txt(o.region)}</td>
                  <td style={td}><Pill status={o.status} id={`r-${id}`} /></td>
                  <td style={{ ...td, textAlign: "right", ...mono }}>{pair(o.observedOfficeDays, o.eligibleOfficeDays)}</td>
                  <td style={{ ...td, textAlign: "right", ...mono }}>{o.lastReportedDate ? fmtDate(o.lastReportedDate) : NA}</td>
                  <td style={td}>Envoy · {o.mappingState === "mapped" || o.mappingState === "unmapped" ? o.mappingState : UNK.toLowerCase()}</td>
                </tr>
              );
            })} />

          <SectionHead testid="feed-section-excluded" title="Not expected" count={excluded.length} note="- shown so nothing is miscategorized" />
          <Table testid="feed-excluded-table" empty="No offices are excluded this period."
            heads={[["Office"], ["Region"], ["Status"], ["Why no data"], ["Counted in coverage?"]]}
            rows={excluded.map((o, i) => {
              const id = o.officeId ?? i;
              const lc = o.lifecycle ?? {};
              const why = o.lifecycleReason || (lc.servicedOffice ? "Serviced office, no badge source by design" : null);
              return (
                <tr key={id} data-testid={`feed-excluded-row-${id}`} style={{ color: T.mid }}>
                  <td style={{ ...td, fontWeight: T.fwSemibold }}>{txt(o.name, NA)}</td>
                  <td style={td}>{txt(o.region)}</td>
                  <td style={td}><Pill status={o.status} id={`x-${id}`} /></td>
                  <td style={td}>{why ?? NA}</td>
                  <td style={td}>{typeof o.countedInCoverage === "boolean" ? (o.countedInCoverage ? "Yes" : "No, correctly excluded") : UNK}</td>
                </tr>
              );
            })} />

          <p style={{ color: T.light, fontSize: T.fsXs, margin: "14px 2px 0" }}>
            Values are computed from the same attendance resolver that produces the headline coverage number (eligible vs observed office-days, per office). Aggregate office-level data only.
          </p>
        </>
      )}
    </div>
  );
}

export default AttendanceFeedHealthReport;
